import { BiPlusCircle, BiDotsVerticalRounded, BiImport } from "react-icons/bi";
import { MdOutlineDescription } from "react-icons/md";
import { TranslationKey } from "@typedefs/modulesInterfaces";
import { HealthCheckError, useGetHealthCheckQuery } from "@features/other/healthApiSlice";
import { useEffect, useMemo, useState } from "react";
import { format } from 'date-fns';
import Button from "@components/button/Button";
import Spinner from "@components/spinner/Spinner";
import TranslatableText from "@components/translations/TranslatableText";

export const ProjectDescription = ({ user }: { user: string | null }) => {
	return (
		<section className="pt-1 d-flex f-column">
			<h3 className="welcome-user-header ff-medium-ext">
				<TranslatableText translationKey="main.welcome" /> <span className="capitalize">{user}</span>
			</h3>
			<div className="py-2 fs-11 ff-normal select-project-p">
				<p>
					<TranslatableText translationKey="main.welcome_message_1" />
				</p>
				<p>
					<TranslatableText translationKey="main.welcome_message_2" />
				</p>
				<MaintenanceBanner />
			</div>
		</section>
	);
};

const MaintenanceBanner = () => {
	const { error } = useGetHealthCheckQuery();
	const [errorData, setErrorData] = useState<HealthCheckError>();

	useEffect(() => {
		setErrorData(error as HealthCheckError);
	}, [error]);

	const date = useMemo(() => {
		if (errorData?.data?.maintenance_end_time)
			return new Date(errorData?.data?.maintenance_end_time);
	}, [errorData]);

	const translationKey = date ? 'main.maintenance_banner' : 'main.maintenance_banner_no_end_time';

	return errorData?.status === 503 ? (
		<div className="maintenance-banner w-fit p-2 mt-2">
			<TranslatableText
				style={{ display: "flex" }}
				translationKey={translationKey as TranslationKey}
				translationParams={{
					maintenance_message: errorData?.data?.maintenance_message || 'The tool is under maintenance. Please try again later.',
					maintenance_end_time: date ? format(date, 'PPpp') : ''
				}}
				paramsComponents={[
					<div style={{
						color: "var(--quaternary-dark)",
						margin: "0px 2px 0px 2px",
						fontWeight: "bold",
						opacity: "90%"
					}} />
				]}
			/>
		</div>
	) : null;
};

export const StartNewProjectButton = ({
	handleClick,
}: {
	handleClick: () => void;
}) => {
	return (
		<Button
			className="btn-new-project responsive-btn-height d-flex align-items-center"
			onClick={handleClick}
		>
			<div className="h-100 d-flex align-items-center btn-arrow bg-quat-de p-1">
				<BiPlusCircle size={20} color="var(--light-gray)" />
			</div>
			<TranslatableText className="h-100 fs-11 px-2 d-flex align-items-center ff-medium bg-white" translationKey="project.start_project" />
		</Button>
	);
};

export const ImportProjectButton = ({
	handleClick,
	isLoading,
}: {
	handleClick: () => void;
	isLoading?: boolean;
}) => {
	return (
		<Button
			className="btn-new-project responsive-btn-height d-flex align-items-center"
			onClick={handleClick}
			disabled={isLoading}
		>
			<div className="h-100 d-flex align-items-center btn-arrow bg-quat-de p-1">
				{isLoading ? <Spinner size={20} /> : <BiImport size={20} color="var(--light-gray)" />}
			</div>
			<TranslatableText className="h-100 fs-11 px-2 d-flex align-items-center ff-medium bg-white" translationKey="project.import_project" />
		</Button>
	);
};

export const NoteComponent = ({
	link,
	disabled,
}: {
	link: string;
	disabled?: boolean;
}) => {
	const handleClick = () => { };

	return (
		<div className="w-100 h-100 d-flex align-items-center justify-content-center">
			<Button
				onClick={handleClick}
				disabled={disabled}
				className="bg-white"
				whileHoverProps={{
					scale: 1,
					padding: "0.2rem",
					border: "1px solid black",
					borderRadius: "5px",
				}}
			>
				<MdOutlineDescription
					size={16}
					opacity={disabled ? 0.25 : 1}
					color="var(--darker-gray)"
				/>
			</Button>
		</div>
	);
};

export const ThreeDots = ({ handler }: { handler: () => void }) => {
	return (
		<div className="w-100 h-100 d-flex align-items-center justify-content-center">
			<Button
				onClick={handler}
				className="bg-white"
				whileHoverProps={{
					scale: 1,
				}}
			>
				<BiDotsVerticalRounded size={16} />
			</Button>
		</div>
	);
};

export const HeaderSpan = ({
	name,
	center,
}: {
	name: TranslationKey;
	center?: boolean;
}) => {
	return (
		<span
			className={`fs-11 text-white fw-400 ${center ? " w-100 text-center" : ""
				}`}
		>
			<TranslatableText translationKey={name} />
		</span>
	);
};